import { useMutation } from "@tanstack/react-query";
import { XCircle } from "lucide-react";
import { useCallback, useId, useState } from "react";
import { toast } from "sonner";

import { filesGenerateUploadUrl } from "~/api-refs";
import { Button } from "~/components/ui/button";
import { convexClient } from "~/integrations/convex/provider";
import { parseStorageUploadResponse } from "~/lib/convex-table-id";

export function LogoUploader({
  onValueChange,
  value,
  photoName = "logo",
  placeholderSrc,
  setUploading,
}: {
  value: string | null;
  onValueChange: (value: string | null) => void;
  photoName?: string;
  placeholderSrc?: string;
  setUploading?: (uploading: boolean) => void;
}) {
  const id = useId();
  const [tempUrl, setTempUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const generateUploadUrlMutation = useMutation({
    mutationFn: () => convexClient.mutation(filesGenerateUploadUrl, {}),
  });

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }

      // Validate file size (4MB max)
      if (file.size > 4 * 1024 * 1024) {
        toast.error("Image must be less than 4MB");
        return;
      }

      setIsUploading(true);
      setUploading?.(true);

      try {
        // Create temporary preview URL
        const previewUrl = URL.createObjectURL(file);
        setTempUrl(previewUrl);

        // Step 1: Get upload URL
        const uploadUrl = await generateUploadUrlMutation.mutateAsync();

        // Step 2: Upload file
        const result = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });

        if (!result.ok) {
          throw new Error("Upload failed");
        }

        const storageId = parseStorageUploadResponse(await result.json());

        // Step 3: Save the storage ID as the value
        onValueChange(storageId);

        // Clean up temp URL
        URL.revokeObjectURL(previewUrl);
        setTempUrl(null);
      } catch {
        toast.error(`Error uploading ${photoName}`);
        setTempUrl(null);
      } finally {
        setIsUploading(false);
        setUploading?.(false);
      }
    },
    [generateUploadUrlMutation, onValueChange, photoName, setUploading]
  );

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <img
          alt={
            (tempUrl ?? value)
              ? `Uploaded ${photoName}`
              : `Placeholder ${photoName}`
          }
          className="size-20 rounded-full object-cover object-center"
          height={80}
          src={
            tempUrl ??
            value ??
            placeholderSrc ??
            "/images/avatar-placeholder.svg"
          }
          width={80}
        />
        {isUploading && (
          <div className="bg-background/50 absolute inset-0 flex items-center justify-center rounded-full">
            <div className="border-border border-t-fg-strong h-6 w-6 animate-spin rounded-full border-2" />
          </div>
        )}
        {value && (
          <button
            className="bg-background absolute -top-px -right-px rounded-full p-0.5"
            onClick={() => {
              onValueChange(null);
            }}
            type="button"
          >
            <XCircle className="text-foreground size-5" />
            <span className="sr-only">Remove {photoName}</span>
          </button>
        )}
      </div>
      <Button
        asChild
        className="cursor-pointer"
        type="button"
        variant="outline"
      >
        <label className="label leading-tight" htmlFor={id}>
          Upload {photoName}
        </label>
      </Button>
      <input
        accept="image/*"
        className="hidden"
        id={id}
        onChange={handleFileChange}
        type="file"
      />
    </div>
  );
}
